import { ChevronDown } from 'lucide-react';
import { useCallback } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ChatTranscript } from '@/features/votes/chat';
import { cn } from '@/lib/utils';

import {
  useOnlineEvalDebugSkeleton,
  useOnlineEvalIssueChat,
  useOnlineEvalIssueRequest,
} from '../online-eval.query';

import { JsonView } from './JsonView';

import type {
  OnlineEvalDebugSkeletonStep,
  OnlineEvalIssueChat,
  OnlineEvalSkeletonSection,
} from '../online-eval.types';

type DebugSkeletonSheetProps = {
  issueId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type MessageGroup = {
  messageIndex: number;
  role: string;
  sections: OnlineEvalSkeletonSection[];
};

const tokenTotal = (sections: OnlineEvalSkeletonSection[]) =>
  sections.reduce((total, section) => total + section.token_count, 0);

const groupStep = (step: OnlineEvalDebugSkeletonStep) => {
  const requestSections = step.sections.filter(
    (section) => section.message_index === null
  );
  const messagesByIndex = new Map<number, MessageGroup>();

  for (const section of step.sections) {
    if (section.message_index === null) continue;
    const existing = messagesByIndex.get(section.message_index);
    if (existing) {
      existing.sections.push(section);
    } else {
      messagesByIndex.set(section.message_index, {
        messageIndex: section.message_index,
        role: section.role ?? 'unknown',
        sections: [section],
      });
    }
  }

  const messages = [...messagesByIndex.values()].sort(
    (a, b) => a.messageIndex - b.messageIndex
  );
  return { requestSections, messages };
};

const SectionRows = ({
  sections,
}: {
  sections: OnlineEvalSkeletonSection[];
}) => (
  <div className="space-y-1.5">
    {sections.map((section) => (
      <div
        key={section.index}
        className="flex items-start justify-between gap-4 rounded-md bg-muted/40 px-3 py-2"
      >
        <div className="min-w-0">
          <p className="text-sm font-medium capitalize">
            {section.section_type.replace(/_/g, ' ')}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {section.label}
          </p>
        </div>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {section.token_count.toLocaleString()} tokens
        </span>
      </div>
    ))}
  </div>
);

const RequestSetup = ({
  sections,
}: {
  sections: OnlineEvalSkeletonSection[];
}) => {
  const tools = sections.filter(
    (section) => section.section_type === 'tool_definition'
  );
  const prompts = sections.filter((section) =>
    section.section_type.startsWith('system_prompt_')
  );
  const otherSections = sections.filter(
    (section) =>
      section.section_type !== 'tool_definition' &&
      !section.section_type.startsWith('system_prompt_')
  );

  return (
    <details className="rounded-lg border bg-muted/20">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
        <div>
          <p className="text-sm font-medium">Request setup</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {tools.length} available {tools.length === 1 ? 'tool' : 'tools'} ·{' '}
            {prompts.length} system prompt{' '}
            {prompts.length === 1 ? 'section' : 'sections'}
          </p>
        </div>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {tokenTotal(sections).toLocaleString()} tokens
        </span>
      </summary>

      <div className="space-y-4 border-t px-4 py-3">
        {tools.length > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Available tools
              </p>
              <span className="text-xs tabular-nums text-muted-foreground">
                {tokenTotal(tools).toLocaleString()} tokens
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tools.map((tool) => (
                <Badge key={tool.index} variant="secondary">
                  {tool.label.replace(/^tool def:\s*/, '')}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {prompts.length > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                System prompt
              </p>
              <span className="text-xs tabular-nums text-muted-foreground">
                {tokenTotal(prompts).toLocaleString()} tokens
              </span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {prompts.map((prompt) => (
                <span key={prompt.index}>
                  {prompt.section_type === 'system_prompt_static'
                    ? 'Static'
                    : 'Dynamic'}{' '}
                  · {prompt.token_count.toLocaleString()} tokens
                </span>
              ))}
            </div>
          </div>
        )}

        {otherSections.length > 0 && <SectionRows sections={otherSections} />}
      </div>
    </details>
  );
};

const Step = ({ step }: { step: OnlineEvalDebugSkeletonStep }) => {
  const { requestSections, messages } = groupStep(step);
  const totalTokens = tokenTotal(step.sections);

  return (
    <details
      className="group rounded-lg border bg-card"
      open={step.step_index === 0}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-4 [&::-webkit-details-marker]:hidden">
        <div className="flex min-w-0 items-center gap-3">
          <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" />
          <div className="min-w-0">
            <p className="font-medium">Request step {step.step_index}</p>
            <p className="truncate text-xs text-muted-foreground">
              {step.model ?? 'Unknown model'}
            </p>
          </div>
        </div>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {totalTokens.toLocaleString()} tokens
        </span>
      </summary>

      <div className="space-y-5 border-t p-4">
        {requestSections.length > 0 && (
          <RequestSetup sections={requestSections} />
        )}

        <section>
          <h4 className="mb-3 text-sm font-medium">Model input snapshot</h4>
          <div className="space-y-3 border-l pl-4">
            {messages.map((message) => (
              <div key={message.messageIndex} className="relative">
                <span className="absolute -left-[1.3rem] top-3 h-2 w-2 rounded-full border bg-background" />
                <div className="rounded-lg border p-3">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs tabular-nums text-muted-foreground">
                        #{message.messageIndex + 1}
                      </span>
                      <Badge variant="secondary" className="capitalize">
                        {message.role.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {tokenTotal(message.sections).toLocaleString()} tokens
                    </span>
                  </div>
                  <SectionRows sections={message.sections} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </details>
  );
};

const FlaggedChat = ({ chat }: { chat: OnlineEvalIssueChat }) => {
  // The flagged message scrolls itself into view as it attaches, which covers both the first
  // open, where the transcript mounts once its query resolves, and a reopen, where the cached
  // transcript mounts with the sheet. Waiting a frame puts the scroll after the sheet's opening
  // focus, which lands on the close button at the top and would otherwise win.
  const scrollFlaggedIntoView = useCallback(
    (message: HTMLDivElement | null) => {
      if (!message) return;
      requestAnimationFrame(() =>
        message.scrollIntoView({ behavior: 'auto', block: 'center' })
      );
    },
    []
  );

  return (
    <section>
      <div className="mb-3 rounded-lg border bg-muted/30 p-4">
        <p className="text-sm font-medium">{chat.title || 'Untitled chat'}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          This chat is yours, so the whole conversation is shown. The flagged
          reply is highlighted.
        </p>
      </div>
      <ChatTranscript
        chatHistory={chat.chat_history}
        highlightedMessageId={chat.flagged_message_id}
        highlightLabel="Flagged"
        highlightedMessageRef={scrollFlaggedIntoView}
      />
    </section>
  );
};

export const DebugSkeletonSheet = ({
  issueId,
  open,
  onOpenChange,
}: DebugSkeletonSheetProps) => {
  const query = useOnlineEvalDebugSkeleton(issueId, open);
  const chatQuery = useOnlineEvalIssueChat(issueId, open);
  const data = query.data;
  const chat = chatQuery.data ?? null;
  // Served only for the admin's own chats, like the chat itself
  const requestQuery = useOnlineEvalIssueRequest(
    issueId,
    open && chat !== null
  );

  const skeleton =
    query.isLoading || chatQuery.isLoading ? (
      <div className="py-16 text-center text-sm text-muted-foreground">
        Loading debug skeleton…
      </div>
    ) : query.error ? (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
        {query.error.message}
      </div>
    ) : data?.steps.length ? (
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-4 rounded-lg border bg-muted/30 p-4">
          <div>
            <p className="text-sm font-medium">Flagged assistant turn</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {new Date(data.turn_message_created_at).toLocaleString()}
            </p>
          </div>
          <Badge variant="secondary">
            {data.steps.length}{' '}
            {data.steps.length === 1 ? 'request step' : 'request steps'}
          </Badge>
        </div>
        <div className="space-y-3">
          {data.steps.map((step) => (
            <Step key={step.payload_id} step={step} />
          ))}
        </div>
      </div>
    ) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className={cn(
          'overflow-y-auto',
          chat ? 'sm:max-w-5xl' : 'sm:max-w-3xl'
        )}
      >
        <SheetHeader className="pr-8">
          <SheetTitle>{chat ? 'Flagged chat' : 'Debug skeleton'}</SheetTitle>
          <SheetDescription>
            {chat
              ? 'Your own conversation, with the content-free request skeleton below it.'
              : 'Content-free snapshots of the model requests that generated the flagged assistant response. Text, prompts, tool results, and filenames are not included.'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {chatQuery.error && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              {chatQuery.error.message}
            </div>
          )}
          {chat && <FlaggedChat chat={chat} />}
          {chat && (
            <details className="rounded-lg border bg-card">
              <summary className="cursor-pointer list-none p-4 text-sm font-medium [&::-webkit-details-marker]:hidden">
                Evaluator request
              </summary>
              <div className="border-t p-4">
                {requestQuery.isLoading ? (
                  <p className="text-sm text-muted-foreground">
                    Loading the evaluator request…
                  </p>
                ) : requestQuery.error ? (
                  <p className="text-sm text-destructive">
                    {requestQuery.error.message}
                  </p>
                ) : requestQuery.data ? (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      What online eval handed the evaluator on{' '}
                      {new Date(requestQuery.data.created_at).toLocaleString()},
                      before the provider wrapped it in its own request format.
                    </p>
                    <JsonView value={requestQuery.data.body} />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No request was kept for this finding. Requests are kept only
                    for internal users&apos; chats, for as long as chat debug
                    payloads are.
                  </p>
                )}
              </div>
            </details>
          )}
          {chat && skeleton ? (
            <details className="rounded-lg border bg-card">
              <summary className="cursor-pointer list-none p-4 text-sm font-medium [&::-webkit-details-marker]:hidden">
                Debug skeleton
              </summary>
              <div className="border-t p-4">{skeleton}</div>
            </details>
          ) : (
            skeleton
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
